import React, { FC, useState, useEffect, ChangeEvent } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, TextField } from '@mui/material';
import { ActionPriceList } from 'models/action';
import { useMutation } from '@apollo/client';
import { modifyActionPriceListTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';

type EditActionPriceListDialogProps = {
    actionPriceList: ActionPriceList;
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onEditActionPriceList: (priceList: ActionPriceList) => void;
    actionPriceLists: ActionPriceList[];
};
const EditActionPriceListDialog: FC<EditActionPriceListDialogProps> = ({
    actionPriceList,
    dialogOpen,
    onCloseDialog,
    onEditActionPriceList,
    actionPriceLists,
}: EditActionPriceListDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const [priceListName, setPriceListName] = useState('');
    const [priceListNameError, setPriceListNameError] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyActionPriceList] = useMutation(modifyActionPriceListTemplate, {
        variables: {
            _id: actionPriceList._id,
            actionPriceListName: priceListName,
        },
        onError(error) {
            showBoundary(error);
        },
        onCompleted: ({ modifyActionPriceList }) => {
            onEditActionPriceList(modifyActionPriceList);
            onCloseDialog();
        },
    });

    const handlePriceListNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPriceListNameError(false);
        if (!event.target.value || actionListNameAlreadyExist(event.target.value)) {
            setPriceListNameError(true);
        }
        setPriceListName(event.target.value);
    };

    const actionListNameAlreadyExist = (name: string) => {
        const actionListsWithoutCurrentPriceList = actionPriceLists.filter(
            (list) =>
                list.actionPriceListName.toLocaleLowerCase().trim() !==
                actionPriceList.actionPriceListName.toLocaleLowerCase().trim(),
        );
        const actionListNames = actionListsWithoutCurrentPriceList.map((actionPriceList) =>
            actionPriceList.actionPriceListName.replace(/\s/g, '').toLocaleLowerCase().trim(),
        );
        if (actionListNames.includes(name.replace(/\s/g, '').toLocaleLowerCase().trim())) {
            return true;
        }
        return false;
    };

    useEffect(() => {
        setPriceListName(actionPriceList.actionPriceListName);
    }, [actionPriceList.actionPriceListName]);

    return (
        <Dialog fullWidth maxWidth="sm" open={dialogOpen}>
            <DialogTitle>{translate('lab-settings:EDIT_PRICE_LIST')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item md={8} sm={12}>
                        <FormControl fullWidth margin="dense">
                            <TextField
                                id="action-list-name"
                                label={translate('lab-settings:PRICE_LIST_NAME')}
                                required
                                defaultValue={priceListName}
                                onChange={handlePriceListNameChange}
                                error={priceListNameError}
                                helperText={
                                    priceListNameError && !priceListName
                                        ? translate('lab-settings:VALIDATION:ACTION_LIST_NAME')
                                        : priceListNameError &&
                                          translate('lab-settings:VALIDATION:ACTION_LIST_ALREADY_EXIST')
                                }
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <Button
                    variant="contained"
                    disabled={priceListName === actionPriceList.actionPriceListName || priceListNameError}
                    onClick={() => modifyActionPriceList()}
                >
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditActionPriceListDialog;
